import { useCallback, useEffect, useRef } from 'react';

interface AudioContextOptions {
  audioElementRef: React.RefObject<HTMLAudioElement | null>;
}

/**
 * Create and return an audio context instance for a given audio element [0].
 * Also returns an associated audio source, a gain node, and play and pause actions
 * which should be used instead of `audioElementRef.current.play/pause()`.
 *
 * [0] https://developer.mozilla.org/en-US/docs/Web/API/AudioContext
 */

export const useAudioContext = ({ audioElementRef }: AudioContextOptions) => {
  const audioContextRef = useRef<AudioContext>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode>(null);
  const gainNodeRef = useRef<GainNode>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    const audioElement = audioElementRef.current;
    if (!audioElement || typeof AudioContext === 'undefined') {
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    let context = audioContextRef.current;
    if (!context || context.state === 'closed') {
      context = new AudioContext();
      audioContextRef.current = context;
      sourceRef.current = null;
    }

    let source = sourceRef.current;
    if (!source) {
      try {
        source = context.createMediaElementSource(audioElement);
      } catch (error) {
        console.warn('Could not create an audio source node', error);
        if (context.state !== 'closed') {
          void context.close();
        }
        if (audioContextRef.current === context) {
          audioContextRef.current = null;
        }
        return;
      }
      sourceRef.current = source;
    }

    const gainNode = context.createGain();
    gainNode.connect(context.destination);
    source.connect(gainNode);

    gainNodeRef.current = gainNode;

    return () => {
      source.disconnect(gainNode);
      gainNode.disconnect();

      closeTimeoutRef.current = setTimeout(() => {
        if (context.state !== 'closed') {
          void context.close();
        }

        if (audioContextRef.current === context) {
          audioContextRef.current = null;
        }
        if (sourceRef.current === source) {
          sourceRef.current = null;
        }
        if (gainNodeRef.current === gainNode) {
          gainNodeRef.current = null;
        }
      }, 0);
    };
  }, [audioElementRef]);

  const playAudio = useCallback(() => {
    if (audioElementRef.current) {
      void audioElementRef.current.play();
      void audioContextRef.current?.resume();
    }
  }, [audioElementRef]);

  const pauseAudio = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      void audioContextRef.current?.suspend();
    }
  }, [audioElementRef]);

  return {
    audioContextRef,
    sourceRef,
    gainNodeRef,
    playAudio,
    pauseAudio,
  };
};
